import React, { useState, useEffect, useRef } from "react";
import { sortBy, uniq } from "underscore";
import Select from 'react-select';
import { DEFAULT_OPTIONS_PAYLOAD } from "./../../common/Table"
import { useGetCompanies, useGetCompanyLocations, useGetDepartmentUserMappings } from "../../../backend/masters";
import { getUserDetails, hasUserAccess } from "../../../backend/auth";
import DateRangeFilter from "./DateRangeFilter";
import { USER_PRIVILEGES } from "../UserManagement/Roles/RoleConfiguration";
import { DashboardView } from "../../../constants/Compliance.constants";
import ComlianceStatusCounts from "./Dashboard/ComlianceStatusCounts";

type Props = {
    onFilterChange: (event: any[]) => void,
    hiddenFilters?: any[],
    view?: DashboardView,
    counts?: any[],
    forNotices?: boolean
}

const DEFAULT_VALUE = 'ALL';
const DEFAULT_LABEL = 'All';
const DEFAULT_OPTION = { value: DEFAULT_VALUE, label: DEFAULT_LABEL };

const DEFAUT_FILTERS = {
    company: DEFAULT_OPTION,
    vertical: DEFAULT_OPTION,
    department: DEFAULT_OPTION
};

export default function ComplianceOwnerFilters({ onFilterChange, view, counts, forNotices }: Props) {
    const isEscalationManager = hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD);
    const [filters, setFilters] = useState<any>({ ...DEFAUT_FILTERS });
    const filtersRef = useRef<any>();
    filtersRef.current = filters;
    const [q, setQ] = useState<any>();
    const [companies, setCompanies] = useState<any[]>(forNotices || isEscalationManager ? [] : [DEFAULT_OPTION]);
    const [associateCompanies, setAssociateCompanies] = useState<any[]>();
    const [locations, setLocations] = useState<any[]>();
    const [verticals, setVerticals] = useState<any[]>([]);
    const [departments, setDepartments] = useState<any[]>([]);
    const [owners, setOwners] = useState<any[]>([]);
    const [managers, setManagers] = useState<any[]>([]);

    const { departmentUsers, isFetching: fetchingCompanies }: any = useGetDepartmentUserMappings({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: {}
    });

    const { companies: acs, isFetching: fetchingAcs } = useGetCompanies({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: {
            is_parent_eq: 'false',
            parent_company_id_eq: (filters.company || {}).value
        }
    }, enableAcs());

    const { locations: locs, isFetching: fetchingLocs } = useGetCompanyLocations({
        ...DEFAULT_OPTIONS_PAYLOAD,
        filters: {company_id_eq:(filters.associate_company || {}).value },
        include: ['location'],
        includes: ['location'],
        sort: ['location_name asc']
    }, enableLocations());

    function enableAcs() {
        const { company } = filters;
        return company && company.value !== DEFAULT_VALUE;
    }

    function enableLocations() {
        const { associate_company } = filters;
        return Boolean(associate_company && associate_company.value && associate_company.value !== DEFAULT_VALUE);
    }

    function handleCompanyChange(newValue: any) {
        const { value } = newValue;
        const isSelectAll = value === DEFAULT_VALUE;
        const { vertical, department, owner, manager } = filters;
        const definedNull = { ...DEFAULT_OPTION };
        setAssociateCompanies(undefined);
        setLocations(undefined);
        setFilters({
            ...filters,
            company: newValue,
            associate_company: isSelectAll ? null : definedNull,
            location: isSelectAll ? null : definedNull,
            vertical: isSelectAll ? vertical : (vertical ? (vertical.company_id === value ? vertical : definedNull) : definedNull),
            department: isSelectAll ? department : (department ? (department.company_id === value ? department : definedNull) : definedNull),
            owner: isSelectAll ? owner : (owner ? (owner.company_id === value ? owner : definedNull) : definedNull),
            manager: isSelectAll ? manager : (manager ? (manager.company_id === value ? manager : definedNull) : definedNull)
        });
    }

    function handleAssociateCompanyChange(newValue: any) {
        const { value } = newValue;
        const definedNull = { ...DEFAULT_OPTION };
        const isSelectAll = value === DEFAULT_VALUE;
        setLocations(undefined);
        setFilters({
            ...filters,
            associate_company: newValue,
            location: isSelectAll ? null : definedNull,
        });
    }

    function handleLocationChange(newValue: any) {
        setFilters({
            ...filters,
            location: newValue
        });
    }

    function handleVerticalChange(newValue: any) {
        const { value } = newValue;
        const isSelectAll = value === DEFAULT_VALUE;
        const { department, owner, manager } = filters;
        const definedNull = { ...DEFAULT_OPTION };
        setFilters({
            ...filters,
            vertical: newValue,
            department: isSelectAll ? undefined : (department ? (department.verticalId === value ? department : definedNull) : definedNull),
            owner: isSelectAll ? undefined : (owner ? (owner.verticalId === value ? owner : definedNull) : definedNull),
            manager: isSelectAll ? undefined : (manager ? (manager.verticalId === value ? manager : definedNull) : definedNull)
        });
    }

    function handleDepartmentChange(newValue: any) {
        const { value } = newValue;
        const isSelectAll = value === DEFAULT_VALUE;
        const { owner, manager } = filters;
        const definedNull = { ...DEFAULT_OPTION };
        setFilters({
            ...filters,
            department: newValue,
            owner: isSelectAll ? undefined : (owner ? (owner.departmentId === value ? owner : definedNull) : definedNull),
            manager: isSelectAll ? undefined : (manager ? (manager.departmentId === value ? manager : definedNull) : definedNull)
        });
    }

    function handleDateRangeChange(range: any) {
        setFilters({
            ...filters,
            ...range
        })
    }

    useEffect(() => {
        if (filters) {
            const {
                start_date_gteq, start_date_lteq, company, associate_company, location,
                vertical, department, owner, manager, isNotice
            } = filtersRef.current;
            const _filters: any = {};
            if (start_date_gteq) {
                _filters['start_date_gteq'] = start_date_gteq;
            }
            if (start_date_lteq) {
                _filters['start_date_lteq'] = start_date_lteq;
            }
            if (company.value !== DEFAULT_VALUE) {
                _filters['company_id_eq'] = company.value;
            }
            if (associate_company && associate_company.value && associate_company.value !== DEFAULT_VALUE) {
                _filters['associate_company_id_eq'] = associate_company.value;
            }
            if (location && location.value && location.value !== DEFAULT_VALUE) {
                _filters['location_id_eq'] = location.value;
            }
            if (vertical && vertical.value && vertical.value !== DEFAULT_VALUE) {
                _filters['veritical_id_eq'] = vertical.value;
            }
            if (department && department.value && department.value !== DEFAULT_VALUE) {
                _filters['department_id_eq'] = department.value;
            }
            if (owner && owner.value && owner.value !== DEFAULT_VALUE) {
                _filters['compliance_owner_id_eq'] = owner.value;
            }
            if (manager && manager.value && manager.value !== DEFAULT_VALUE) {
                _filters['compliance_manager_id_eq'] = manager.value;
            }
            if (isNotice) {
                _filters['is_notice_eq'] = 'true';
            }
            setQ(_filters);
            onFilterChange(_filters);
        }
    }, [filters]);

    useEffect(() => {
        if (!fetchingLocs && locs) {
            setLocations([DEFAULT_OPTION, ...locs.map(({ location }: any) => {
                return { value: location.id, label: location.name }
            })]);
        }
    }, [fetchingLocs]);

    useEffect(() => {
        if (!fetchingCompanies && acs) {
            setLocations(undefined);
            const sorted = sortBy(acs.map(({ id, name }: any) => {
                return { value: id, label: name }
            }), 'label');
            setAssociateCompanies([DEFAULT_OPTION, ...sorted]);
        }
    }, [fetchingAcs]);

    useEffect(() => {
        if (!fetchingCompanies && departmentUsers) {
            const _companies: any = {};
            const _verticals: any = {};
            const _departments: any = {};
            const _owners: any = [];
            const _managers: any = [];
            setAssociateCompanies(undefined);
            setLocations(undefined);
            const { user_id: loginUser } = getUserDetails();
            departmentUsers.forEach(({ department, user_id, user }: any) => {
                const { vertical } = department;
                const { company } = vertical;
                const { id, name } = company;
                if (!_companies[id] && user_id === loginUser) {
                    _companies[id] = { value: id, label: name };
                }

                if (!_verticals[vertical.id] && _companies[id]) {
                    _verticals[vertical.id] = {
                        value: vertical.id,
                        label: vertical.name,
                        company_id: company.id,
                        code: `${company.code}-${vertical.shortCode}`
                    }
                }

                if (!_departments[department.id] && _companies[id]) {
                    _departments[department.id] = {
                        value: department.id,
                        label: department.name,
                        company_id: company.id,
                        verticalId: vertical.id,
                        code: `${company.code}-${vertical.shortCode}-${department.shortCode}`
                    }
                }
                const isOwner = Boolean(user.roles.find(({ pages }: any) => pages.includes(USER_PRIVILEGES.OWNER_DASHBOARD)));
                const isManager = Boolean(user.roles.find(({ pages }: any) => pages.includes(USER_PRIVILEGES.MANAGER_DASHBOARD)));
                // const uniqueCode = `${company.code}-${vertical.shortCode}-${department.shortCode}-${userId}`
                if (isOwner || forNotices || isEscalationManager) {
                    _owners.push({
                        value: user.id,
                        label: user.name,
                        company_id: company.id,
                        // verticalId: vertical.id,
                        // departmentId: department.id,
                        // uniqueCode,
                        // code: `${company.code}-${vertical.shortCode}-${department.shortCode}`
                    });
                }
                if (isManager || forNotices || isEscalationManager) {
                    _managers.push({
                        value: user.id,
                        label: user.name,
                        company_id: company.id,
                        // verticalId: vertical.id,
                        // departmentId: department.id,
                        // uniqueCode,
                        // code: `${company.code}-${vertical.shortCode}-${department.shortCode}`
                    });
                }
            });

            const comps: any[] = sortBy(Object.values(_companies), 'label');
            if (forNotices || isEscalationManager) {
                setCompanies(comps);
                if (comps.length > 0) {
                    setFilters({
                        ...filters,
                        company: comps[0]
                    });
                }
            } else {
                setCompanies([DEFAULT_OPTION, ...comps]);
            }
            setVerticals(sortBy(Object.values(_verticals), 'label'));
            setDepartments(sortBy(Object.values(_departments), 'label'));
            setOwners(sortBy(uniq(_owners.filter(({ company_id }: any) => Boolean(_companies[company_id])), true, (user) => user.value), 'label'));
            setManagers(sortBy(uniq(_managers.filter(({ company_id }: any) => Boolean(_companies[company_id])), true, (user) => user.value), 'label'));
        }
    }, [fetchingCompanies])

    return (
        <>
            <div className="d-flex flex-column ">
                <div className="d-flex flex-row flex-wrap filters align-items-end ps-2 gap-2" style={{ paddingRight: '50px' }}>
                    <div>
                        <label className="filter-label"><small>Company</small></label>
                        <Select placeholder='Company' className="select-control"
                            options={companies} onChange={handleCompanyChange}
                            value={filters.company} />
                    </div>
                    <div>
                        <label className="filter-label"><small>Associate Company</small></label>
                        <Select placeholder={DEFAULT_LABEL} className="select-control" isDisabled={!Boolean(associateCompanies)}
                            options={associateCompanies} onChange={handleAssociateCompanyChange}
                            value={filters.associate_company} />
                    </div>
                    <div>
                        <label className="filter-label"><small>Location</small></label>
                        <Select placeholder={DEFAULT_LABEL} className="select-control" isDisabled={!Boolean(locations)}
                            options={locations} onChange={handleLocationChange}
                            value={filters.location} />
                    </div>
                    <div>
                        <label className="filter-label"><small>Vertical</small></label>
                        <Select placeholder={DEFAULT_LABEL} className="select-control"
                            onChange={handleVerticalChange} value={filters.vertical}
                            options={[DEFAULT_OPTION, ...verticals.filter(({ company_id }: any) => {
                                const { company } = filtersRef.current;
                                return company.value === DEFAULT_VALUE ? true : company_id === company.value;
                            })]} />
                    </div>
                    <div>
                        <label className="filter-label"><small>Department</small></label>
                        <Select placeholder={DEFAULT_LABEL} className="select-control"
                            onChange={handleDepartmentChange} value={filters.department}
                            options={[DEFAULT_OPTION, ...departments.filter(({ company_id, verticalId }: any) => {
                                const { company, vertical } = filtersRef.current;
                                if (vertical.value !== DEFAULT_VALUE) {
                                    return verticalId === vertical.value;
                                }
                                if (company.value !== DEFAULT_VALUE) {
                                    return company_id === company.value;
                                }
                                return true;
                            })]} />
                    </div>
                    {
                        !forNotices && (hasUserAccess(USER_PRIVILEGES.MANAGER_DASHBOARD) || hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD)) &&
                        <div>
                            <label className="filter-label"><small>Owner</small></label>
                            <Select placeholder={DEFAULT_LABEL} className="select-control"
                                value={filters.owner} options={[DEFAULT_OPTION, ...owners]}
                                onChange={(owner: any) => {
                                    setFilters({ ...filtersRef.current, owner })
                                }}
                            />
                        </div>
                    }
                    {
                        !forNotices && (hasUserAccess(USER_PRIVILEGES.OWNER_DASHBOARD) || hasUserAccess(USER_PRIVILEGES.ESCALATION_DASHBOARD)) &&
                        <div>
                            <label className="filter-label"><small>Manager</small></label>
                            <Select placeholder={DEFAULT_LABEL} className="select-control"
                                options={[DEFAULT_OPTION, ...managers]} value={filters.manager}
                                onChange={(manager: any) => {
                                    setFilters({ ...filtersRef.current, manager })
                                }} />
                        </div>
                    }
                    {
                        !forNotices &&
                        <>
                            <div className="px-2 h-100"></div>
                            <DateRangeFilter onDateRangeChange={handleDateRangeChange} />
                            {
                                view === DashboardView.CALENDAR &&
                                <ComlianceStatusCounts filters={q} counts={counts} />
                            }
                        </>
                    }
                </div>
            </div>
        </>
    )
}
